import axios from "axios";
import {
  ClothingItem,
  OutfitSuggestion,
  OccasionType,
  ClothingType,
} from "../types/wardrobe";
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

// Demo mode helper functions
const isDemoMode = (): boolean => {
  return localStorage.getItem("demoMode") === "true";
};

const generateDemoId = (): string => {
  return `demo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Demo wardrobe items are now fetched from the database

// Debug log to verify API URL

// Always use /api as the prefix, both locally and in production
const getEndpoint = (path: string) => `/api${path}`;

// Create axios instance with auth header interceptor
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests if available
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

interface AuthResponse {
  user: {
    _id: string;
    email: string;
    name: string;
  };
  token: string;
}

interface ImageAnalysisResult {
  dominantColors: string[];
  patterns: string[];
  attributes: {
    texture?: string;
    style?: string;
    material?: string;
    type?: string;
    occasion?: string;
  };
  seasons: string[];
}

interface ImageUploadResponse {
  imageUrl: string;
  imageId: string;
}

interface CreateClothingItemData {
  name: string;
  type: string;
  category: string;
  occasion: string;
  imageUrl: string;
  imageId: string;
  seasons: string[];
  dominantColors?: string[];
  patterns?: string[];
  attributes?: {
    texture?: string;
    style?: string;
    material?: string;
  };
}

export const api = {
  register: async (
    email: string,
    password: string,
    name: string
  ): Promise<AuthResponse> => {
    const response = await axiosInstance.post(getEndpoint("/auth/register"), {
      email,
      password,
      name,
    });
    return response.data;
  },

  login: async (email: string, password: string): Promise<AuthResponse> => {
    const response = await axiosInstance.post(getEndpoint("/auth/login"), {
      email,
      password,
    });
    return response.data;
  },

  uploadClothing: async (
    file: File,
    itemDetails: Partial<ClothingItem>
  ): Promise<ClothingItem> => {
    // Create form data with file and item details
    const formData = new FormData();
    formData.append("image", file);
    formData.append("name", itemDetails.name || file.name.split(".")[0]);

    // Upload image and create item in one step
    const response = await axiosInstance.post<ClothingItem>(
      getEndpoint("/upload"),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        // Add upload progress tracking
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 100)
          );
        },
      }
    );

    return response.data;
  },

  getWardrobe: async (): Promise<ClothingItem[]> => {
    if (isDemoMode()) {
      // Fetch demo items from database instead of static data
      const response = await axios.get(`${API_URL}/api/demo/wardrobe`);
      return response.data;
    }
    const response = await axiosInstance.get(getEndpoint("/wardrobe"));
    return response.data;
  },

  getSuggestions: async (
    season?: string,
    occasion?: string
  ): Promise<OutfitSuggestion[]> => {
    if (isDemoMode()) {
      // Fetch demo items from database for suggestions
      const response = await axios.get(`${API_URL}/api/demo/wardrobe`);
      const demoItems = response.data;

      // Return mock suggestions for demo mode
      return [
        {
          _id: "demo-suggestion-1",
          userId: "demo-user-id",
          items: demoItems,
          score: 0.85,
          season: season || "spring",
          occasions: occasion ? [occasion as OccasionType] : ["casual"],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      ];
    }

    const params = new URLSearchParams();
    if (season) params.append("season", season);
    if (occasion) params.append("occasion", occasion);

    const response = await axiosInstance.get(getEndpoint("/suggestions"), {
      params,
    });
    return response.data.suggestions;
  },

  deleteClothingItem: async (itemId: string): Promise<void> => {
    if (isDemoMode()) {
      // In demo mode, we don't allow deleting items from the demo database
      // This maintains the demo dataset integrity
      throw new Error("Cannot delete items in demo mode");
    }
    await axiosInstance.delete(getEndpoint(`/wardrobe/${itemId}`));
  },

  reanalyzeItem: async (itemId: string): Promise<ClothingItem> => {
    if (isDemoMode()) {
      // In demo mode, we don't allow re-analyzing items
      // This maintains the demo dataset integrity
      throw new Error("Cannot re-analyze items in demo mode");
    }
    const response = await axiosInstance.post<ClothingItem>(
      getEndpoint(`/wardrobe/reanalyze/${itemId}`)
    );
    return response.data;
  },

  updateClothingItem: async (item: ClothingItem): Promise<ClothingItem> => {
    if (isDemoMode()) {
      // In demo mode, we don't allow updating items
      // This maintains the demo dataset integrity
      throw new Error("Cannot update items in demo mode");
    }

    const response = await axiosInstance.put<ClothingItem>(
      getEndpoint(`/wardrobe/${item._id}`),
      {
        name: item.name,
        type: item.type,
        category: item.category,
        occasions: item.occasions,
        seasons: item.seasons,
        dominantColors: item.dominantColors,
        patterns: item.patterns,
        attributes: item.attributes,
      }
    );
    return response.data;
  },

  addToWardrobe: async (formData: FormData): Promise<ClothingItem> => {
    if (isDemoMode()) {
      // In demo mode, we don't allow adding new items
      // This maintains the demo dataset integrity
      throw new Error("Cannot add items in demo mode");
    }

    const response = await axiosInstance.post<ClothingItem>(
      getEndpoint("/wardrobe"),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  updateWardrobeItem: async (
    id: string,
    data: Partial<ClothingItem>
  ): Promise<ClothingItem> => {
    const response = await axiosInstance.put<ClothingItem>(
      getEndpoint(`/wardrobe/${id}`),
      data
    );
    return response.data;
  },

  getRateLimitStatus: async (): Promise<{
    isExempt: boolean;
    limit: number;
    remaining: number;
    resetTime: number | null;
  }> => {
    if (isDemoMode()) {
      // Demo mode has unlimited uploads
      return {
        isExempt: true,
        limit: 100,
        remaining: 100,
        resetTime: null,
      };
    }

    const response = await axiosInstance.get(getEndpoint("/rate-limit-status"));
    return response.data;
  },
};
